import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  getEffectiveMenus: () => api.get('/auth/effective-menus'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Fault Log APIs
export const faultsAPI = {
  create: (data) => api.post('/faults', data),
  getAll: (params) => api.get('/faults', { params }),
  getOne: (id) => api.get(`/faults/${id}`),
  update: (id, data) => api.put(`/faults/${id}`, data),
  delete: (id) => api.delete(`/faults/${id}`),
  getComplaintTypes: () => api.get('/fault-complaint-types'),
  getStatuses: () => api.get('/fault-statuses'),
};

export const jobsAPI = {
  create: (data) => api.post('/jobs', data),
  getAll: (params = {}) => api.get('/jobs', { params }),
  getOne: (id) => api.get(`/jobs/${id}`),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/jobs/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadMultipleAttachments: (id, files, description = '') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('description', description);
    return api.post(`/jobs/${id}/attachments/multi`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (jobId, attachmentId) => api.delete(`/jobs/${jobId}/attachments/${attachmentId}`),
  // Gallery endpoints
  uploadGallery: (id, files, description = '') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('description', description);
    return api.post(`/jobs/${id}/gallery`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getGallery: (id, search = '') => api.get(`/jobs/${id}/gallery`, { params: { search } }),
  deleteGalleryItem: (jobId, itemId) => api.delete(`/jobs/${jobId}/gallery/${itemId}`),
  addComment: (id, comment) => api.post(`/jobs/${id}/comments`, { comment }),
  getComments: (id) => api.get(`/jobs/${id}/comments`),
  // Project Tasks (Program Module)
  getTasks: (jobId) => api.get(`/jobs/${jobId}/tasks`),
  createTask: (jobId, data) => api.post(`/jobs/${jobId}/tasks`, data),
  updateTask: (jobId, taskId, data) => api.put(`/jobs/${jobId}/tasks/${taskId}`, data),
  deleteTask: (jobId, taskId) => api.delete(`/jobs/${jobId}/tasks/${taskId}`),
  bulkUpdateTasks: (jobId, tasks) => api.post(`/jobs/${jobId}/tasks/bulk-update`, { tasks }),
  indentTask: (jobId, taskId, direction) => api.post(`/jobs/${jobId}/tasks/${taskId}/indent`, null, { params: { direction } }),
  insertTaskRow: (jobId, taskId, position) => api.post(`/jobs/${jobId}/tasks/${taskId}/insert`, null, { params: { position } }),
  // Task Resources (Phase 3)
  addTaskResource: (jobId, taskId, data) => api.post(`/jobs/${jobId}/tasks/${taskId}/resources`, data),
  updateTaskResource: (jobId, taskId, resourceId, data) => api.put(`/jobs/${jobId}/tasks/${taskId}/resources/${resourceId}`, data),
  removeTaskResource: (jobId, taskId, resourceId) => api.delete(`/jobs/${jobId}/tasks/${taskId}/resources/${resourceId}`),
  // Task Materials (Phase 3)
  addTaskMaterial: (jobId, taskId, data) => api.post(`/jobs/${jobId}/tasks/${taskId}/materials`, data),
  updateTaskMaterial: (jobId, taskId, materialId, data) => api.put(`/jobs/${jobId}/tasks/${taskId}/materials/${materialId}`, data),
  removeTaskMaterial: (jobId, taskId, materialId) => api.delete(`/jobs/${jobId}/tasks/${taskId}/materials/${materialId}`),
  // Task Dependencies (Phase 4)
  getTaskDependencies: (jobId, taskId) => api.get(`/jobs/${jobId}/tasks/${taskId}/dependencies`),
  addTaskDependency: (jobId, taskId, data) => api.post(`/jobs/${jobId}/tasks/${taskId}/dependencies`, data),
  removeTaskDependency: (jobId, taskId, predecessorId) => api.delete(`/jobs/${jobId}/tasks/${taskId}/dependencies/${predecessorId}`),
  getAvailablePredecessors: (jobId, taskId) => api.get(`/jobs/${jobId}/tasks/${taskId}/available-predecessors`),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getTechnicians: () => api.get('/users/technicians'),
  getManagers: () => api.get('/users/managers'),
  getOne: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
};

export const assetsAPI = {
  create: (data) => api.post('/assets', data),
  getAll: (params) => api.get('/assets', { params }),
  getOne: (id) => api.get(`/assets/${id}`),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  issue: (data) => api.post('/assets/issue', data),
  return: (id, data) => api.post(`/assets/${id}/return`, data || {}),
};

export const boqAPI = {
  create: (data) => api.post('/boq', data),
  getAll: () => api.get('/boq'),
  getOne: (id) => api.get(`/boq/${id}`),
  getJobBOQ: (jobId) => api.get(`/jobs/${jobId}/boq`),
  update: (id, data) => api.put(`/boq/${id}`, data),
  getCategories: () => api.get('/boq-categories'),
  submitForApproval: (id) => api.post(`/boq/${id}/submit-for-approval`),
  approve: (id, data) => api.post(`/boq/${id}/approve`, data),
  generateRequisition: (id) => api.post(`/boq/${id}/generate-requisition`),
  addItem: (id, item) => api.post(`/boq/${id}/items`, item),
  deleteItem: (id, itemId) => api.delete(`/boq/${id}/items/${itemId}`),
  // Requisition actions for BOQ items
  requisitionItems: (id, itemIds) => api.post(`/boq/${id}/requisition-items`, { item_ids: itemIds }),
  unrequisitionItems: (id, itemIds) => api.post(`/boq/${id}/unrequisition-items`, { item_ids: itemIds }),
};

export const requisitionsAPI = {
  create: (data) => api.post('/requisitions', data),
  getAll: () => api.get('/requisitions'),
  getOne: (id) => api.get(`/requisitions/${id}`),
  update: (id, data) => api.put(`/requisitions/${id}`, data),
  updateItemAllocation: (reqId, itemId, month) => api.put(`/requisitions/${reqId}/items/${itemId}/allocation`, { month }),
  updateBulkAllocations: (reqId, allocations) => api.put(`/requisitions/${reqId}/bulk-allocations`, allocations),
  addItem: (id, item) => api.post(`/requisitions/${id}/items`, item),
  deleteItem: (id, itemId) => api.delete(`/requisitions/${id}/items/${itemId}`),
};

export const teamsAPI = {
  create: (data) => api.post('/teams', data),
  getAll: () => api.get('/teams'),
  getOne: (id) => api.get(`/teams/${id}`),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
};

export const teamMembersAPI = {
  create: (data) => api.post('/team-members', data),
  getAll: (params) => api.get('/team-members', { params }),
  getOne: (id) => api.get(`/team-members/${id}`),
  update: (id, data) => api.put(`/team-members/${id}`, data),
  delete: (id) => api.delete(`/team-members/${id}`),
  getPerformanceSummary: (id) => api.get(`/team-members/${id}/performance-summary`),
  getSummary: (id) => api.get(`/team-members/${id}/summary`),
  // PPE
  issuePPE: (memberId, data) => api.post(`/team-members/${memberId}/ppe`, data),
  getPPE: (memberId) => api.get(`/team-members/${memberId}/ppe`),
  updatePPE: (memberId, ppeId, data) => api.put(`/team-members/${memberId}/ppe/${ppeId}`, data),
  deletePPE: (memberId, ppeId) => api.delete(`/team-members/${memberId}/ppe/${ppeId}`),
  // Certifications
  addCertification: (memberId, data) => api.post(`/team-members/${memberId}/certifications`, data),
  getCertifications: (memberId) => api.get(`/team-members/${memberId}/certifications`),
  updateCertification: (memberId, certId, data) => api.put(`/team-members/${memberId}/certifications/${certId}`, data),
  deleteCertification: (memberId, certId) => api.delete(`/team-members/${memberId}/certifications/${certId}`),
  // Leave
  addLeave: (memberId, data) => api.post(`/team-members/${memberId}/leave`, data),
  getLeave: (memberId, year) => api.get(`/team-members/${memberId}/leave`, { params: { year } }),
  updateLeave: (memberId, leaveId, data) => api.put(`/team-members/${memberId}/leave/${leaveId}`, data),
  deleteLeave: (memberId, leaveId) => api.delete(`/team-members/${memberId}/leave/${leaveId}`),
  uploadLeaveDocument: (memberId, leaveId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/team-members/${memberId}/leave/${leaveId}/document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const leaveTypesAPI = {
  getAll: () => api.get('/leave-types'),
  add: (name) => api.post('/leave-types', { name }),
};

export const performanceReviewAPI = {
  create: (data) => api.post('/performance-reviews', data),
  getAll: (params) => api.get('/performance-reviews', { params }),
  getOne: (id) => api.get(`/performance-reviews/${id}`),
  update: (id, data) => api.put(`/performance-reviews/${id}`, data),
  getPendingAssignments: () => api.get('/performance-reviews/pending/assignments'),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
};

export const analyticsAPI = {
  getStats: () => api.get('/analytics/stats'),
  getDashboard: () => api.get('/analytics/dashboard'),
  getLeaveConflicts: () => api.get('/analytics/leave-conflicts'),
};

// Sub Contractor APIs
export const subcontractorsAPI = {
  create: (data) => api.post('/subcontractors', data),
  getAll: (params) => api.get('/subcontractors', { params }),
  getOne: (id) => api.get(`/subcontractors/${id}`),
  update: (id, data) => api.put(`/subcontractors/${id}`, data),
  delete: (id) => api.delete(`/subcontractors/${id}`),
  getTypes: () => api.get('/contractor-types'),
};

// Job Sub Contractor Assignment APIs
export const jobSubcontractorsAPI = {
  getAll: (jobId) => api.get(`/jobs/${jobId}/subcontractors`),
  assign: (jobId, data) => api.post(`/jobs/${jobId}/subcontractors`, data),
  update: (jobId, assignmentId, data) => api.put(`/jobs/${jobId}/subcontractors/${assignmentId}`, data),
  remove: (jobId, assignmentId) => api.delete(`/jobs/${jobId}/subcontractors/${assignmentId}`),
  uploadQuote: (jobId, assignmentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/jobs/${jobId}/subcontractors/${assignmentId}/quote`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadAppointmentLetter: (jobId, assignmentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/jobs/${jobId}/subcontractors/${assignmentId}/appointment-letter`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Job Resource Assignments (Time & Attendance)
export const jobAssignmentsAPI = {
  create: (data) => api.post('/job-assignments', data),
  getAll: (params) => api.get('/job-assignments', { params }),
  getOne: (id) => api.get(`/job-assignments/${id}`),
  update: (id, data) => api.put(`/job-assignments/${id}`, data),
  delete: (id) => api.delete(`/job-assignments/${id}`),
  checkConflicts: (data) => api.post('/job-assignments/check-conflicts', data),
};

// Timesheet APIs
export const timesheetsAPI = {
  create: (data) => api.post('/timesheets', data),
  getAll: (params) => api.get('/timesheets', { params }),
  update: (id, data) => api.put(`/timesheets/${id}`, data),
  delete: (id) => api.delete(`/timesheets/${id}`),
};

// Wage Schedule APIs
export const wageSchedulesAPI = {
  generate: (period_start, period_end) => api.post(`/wage-schedules/generate?period_start=${period_start}&period_end=${period_end}`),
  getAll: (params) => api.get('/wage-schedules', { params }),
  getOne: (id) => api.get(`/wage-schedules/${id}`),
  approve: (id) => api.put(`/wage-schedules/${id}/approve`),
  markPaid: (id) => api.put(`/wage-schedules/${id}/mark-paid`),
};

// Cashflow APIs
export const cashflowAPI = {
  getJobCashflow: (jobId) => api.get(`/cashflow/job/${jobId}`),
  getProjection: (startMonth, endMonth, jobIds) => api.get('/cashflow/projection', { 
    params: { start_month: startMonth, end_month: endMonth, job_ids: jobIds } 
  }),
  getSummary: () => api.get('/cashflow/summary'),
};

// Lead Management APIs
export const leadsAPI = {
  create: (data) => api.post('/leads', data),
  getAll: (params) => api.get('/leads', { params }),
  getOne: (id) => api.get(`/leads/${id}`),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  getOptions: () => api.get('/lead-options'),
  uploadQuote: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/leads/${id}/upload-quote`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  addSubcontractorQuote: (id, name, amount, file) => {
    const formData = new FormData();
    formData.append('subcontractor_name', name);
    formData.append('amount', amount);
    formData.append('file', file);
    return api.post(`/leads/${id}/subcontractor-quote`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  addSupplierQuote: (id, name, amount, file) => {
    const formData = new FormData();
    formData.append('supplier_name', name);
    formData.append('amount', amount);
    formData.append('file', file);
    return api.post(`/leads/${id}/supplier-quote`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  convertToJob: (id, data) => api.post(`/leads/${id}/convert-to-job`, data),
  uploadAttachment: (id, file, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    return api.post(`/leads/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadMultipleAttachments: (id, files, description = '') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('description', description);
    return api.post(`/leads/${id}/attachments/multi`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (leadId, attachmentId) => api.delete(`/leads/${leadId}/attachments/${attachmentId}`),
  // Assignment endpoints
  getAssignmentTypes: () => api.get('/lead-assignment-types'),
  getAssignments: (leadId) => api.get(`/leads/${leadId}/assignments`),
  createAssignment: (leadId, data) => api.post(`/leads/${leadId}/assignments`, data),
  updateAssignment: (leadId, assignmentId, data) => api.put(`/leads/${leadId}/assignments/${assignmentId}`, data),
  deleteAssignment: (leadId, assignmentId) => api.delete(`/leads/${leadId}/assignments/${assignmentId}`),
  // Gallery endpoints
  uploadGallery: (id, files, description = '') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('description', description);
    return api.post(`/leads/${id}/gallery`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getGallery: (id, search = '') => api.get(`/leads/${id}/gallery`, { params: { search } }),
  deleteGalleryItem: (leadId, itemId) => api.delete(`/leads/${leadId}/gallery/${itemId}`),
};

// Clock In/Out APIs
export const clockAPI = {
  clockIn: (data) => api.post('/clock/in', data),
  clockOut: (entryId, data) => api.post(`/clock/${entryId}/out`, data),
  getEntries: (params) => api.get('/clock/entries', { params }),
  getMyStatus: (memberId) => api.get(`/clock/my-status?member_id=${memberId}`),
  getExceptions: (params) => api.get('/clock/exceptions', { params }),
  resolveException: (id, notes) => api.put(`/clock/exceptions/${id}/resolve?resolution_notes=${encodeURIComponent(notes)}`),
  checkExceptions: () => api.post('/clock/check-exceptions'),
  // Self clock-in/out for site_worker users
  selfClockIn: (data) => api.post('/clock/self/in', data),
  selfClockOut: (data) => api.post('/clock/self/out', data),
  getSelfStatus: () => api.get('/clock/self/status'),
};

// Reports APIs
export const reportsAPI = {
  getAttendanceExceptions: (params) => api.get('/reports/attendance-exceptions', { params }),
  getJobsMissingResources: (daysAhead = 8) => api.get(`/reports/jobs-missing-resources?days_ahead=${daysAhead}`),
  getLeadsExpiring: (params) => api.get('/reports/leads-expiring', { params }),
  getSalesConversion: (params) => api.get('/reports/sales-conversion', { params }),
};

// User Management APIs (Admin)
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  createUser: (userData) => api.post('/admin/users/create', userData),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
  updateUserStatus: (userId, active) => api.put(`/admin/users/${userId}/status?active=${active}`),
  updateUserSecurity: (userId, securityData) => api.put(`/admin/users/${userId}/security`, securityData),
  resetUserPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  resendInvite: (userId) => api.post(`/admin/users/${userId}/resend-invite`),
  bulkImportUsers: (users) => api.post('/admin/users/bulk-import', { users }),
  getMenuItems: () => api.get('/admin/menu-items'),
  getUserRoles: () => api.get('/admin/user-roles'),
  updateUserRoles: (roles) => api.put('/admin/user-roles', roles),
  // Database management
  getDatabaseStats: () => api.get('/admin/database/stats'),
  purgeDatabase: (purgeData) => api.post('/admin/database/purge', purgeData),
  // ===== Access Model V2 =====
  listMenus: () => api.get('/menus'),
  updateUserPermissions: (userId, payload) => api.put(`/admin/users/${userId}/permissions`, payload),
  getArchivedJobs: () => api.get('/admin/archived-jobs'),
  unarchiveJob: (jobId) => api.post(`/admin/jobs/${jobId}/unarchive`),
};

// Asset maintenance APIs
export const assetMaintenanceAPI = {
  getTypes: () => api.get('/assets/types'),
  logMaintenance: (assetId, data) => api.post(`/assets/${assetId}/maintenance`, data),
  getMaintenanceHistory: (assetId) => api.get(`/assets/${assetId}/maintenance`),
  getMaintenanceDue: () => api.get('/assets/maintenance-due'),
};

// POPIA Compliance APIs
export const popiaAPI = {
  recordConsent: (consentType, consented) => api.post(`/popia/consent?consent_type=${consentType}&consented=${consented}`),
  getMyConsents: () => api.get('/popia/consent'),
  createDataRequest: (requestType, description) => api.post(`/popia/data-request?request_type=${requestType}&description=${encodeURIComponent(description || '')}`),
  getDataRequests: (params) => api.get('/popia/data-requests', { params }),
  processDataRequest: (requestId, status, notes) => api.put(`/popia/data-requests/${requestId}?status=${status}&response_notes=${encodeURIComponent(notes || '')}`),
  exportMyData: () => api.get('/popia/my-data'),
  getPrivacyPolicy: () => api.get('/popia/privacy-policy'),
};

// Audit Log APIs
export const auditAPI = {
  getLogs: (params) => api.get('/audit-logs', { params }),
};

// Incident Reporting APIs (OHS)
export const incidentAPI = {
  create: (data) => api.post('/incidents', data),
  getAll: (params) => api.get('/incidents', { params }),
  getOne: (id) => api.get(`/incidents/${id}`),
  update: (id, data) => api.put(`/incidents/${id}`, data),
  getStats: () => api.get('/incidents/stats/summary'),
};

// System Settings APIs
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Daily Site Diary APIs
export const siteDiaryAPI = {
  getEnums: () => api.get('/site-diary/enums'),
  list: (params = {}) => api.get('/site-diary', { params }),
  create: (data) => api.post('/site-diary', data),
  getOne: (id) => api.get(`/site-diary/${id}`),
  updateHeader: (id, data) => api.patch(`/site-diary/${id}`, data),
  remove: (id) => api.delete(`/site-diary/${id}`),
  // child line-items: entity in labor|equipment|deliveries|progress|visitors
  addRow: (id, entity, data) => api.post(`/site-diary/${id}/${entity}`, data),
  updateRow: (id, entity, rowId, data) => api.patch(`/site-diary/${id}/${entity}/${rowId}`, data),
  deleteRow: (id, entity, rowId) => api.delete(`/site-diary/${id}/${entity}/${rowId}`),
  saveSafety: (id, data) => api.put(`/site-diary/${id}/safety`, data),
  // workflow
  submit: (id) => api.post(`/site-diary/${id}/submit`),
  submitByProject: (projectId, diaryDate) => api.post(`/v1/projects/${projectId}/site-diary`, null, { params: { diary_date: diaryDate } }),
  signOff: (id, data) => api.post(`/site-diary/${id}/sign-off`, data),
  lock: (id, data) => api.patch(`/v1/site-diary/${id}/lock`, data || {}),
  reject: (id, comments) => api.post(`/site-diary/${id}/reject`, { comments }),
  reopen: (id) => api.post(`/site-diary/${id}/reopen`),
  voidDiary: (id, reason) => api.post(`/site-diary/${id}/void`, { reason }),
  auditLog: (id) => api.get(`/site-diary/${id}/audit-log`),
  amendmentDiff: (id) => api.get(`/site-diary/${id}/amendment-diff`),
  pdfUrl: (id) => `${API_URL}/site-diary/${id}/pdf`,
  getPdfBlob: (id) => api.get(`/site-diary/${id}/pdf`, { responseType: 'blob' }),
  // media
  listMedia: (id) => api.get(`/site-diary/${id}/media`),
  uploadMedia: (id, file, meta = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', meta.category || 'general');
    fd.append('caption', meta.caption || '');
    fd.append('linked_entity_type', meta.linked_entity_type || 'none');
    fd.append('linked_entity_id', meta.linked_entity_id || '');
    return api.post(`/site-diary/${id}/media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  mediaFileUrl: (id, mediaId) => `${API_URL}/site-diary/${id}/media/${mediaId}/file`,
  getMediaBlob: (id, mediaId) => api.get(`/site-diary/${id}/media/${mediaId}/file`, { responseType: 'blob' }),
  deleteMedia: (id, mediaId) => api.delete(`/site-diary/${id}/media/${mediaId}`),
  // analytics
  laborUtilization: (params = {}) => api.get('/v1/analytics/labor-utilization', { params }),
  attrition: (params = {}) => api.get('/site-diary/analytics/attrition', { params }),
  weatherDowntime: (params = {}) => api.get('/site-diary/analytics/weather-downtime', { params }),
  syncRecords: (diaryId) => api.get('/site-diary/sync/records', { params: diaryId ? { diary_id: diaryId } : {} }),
  // photo compare + client portal
  compare: (id) => api.get(`/site-diary/${id}/compare`),
  share: (id) => api.post(`/site-diary/${id}/share`),
  publicGet: (token) => api.get(`/site-diary/public/${token}`),
  publicMediaBlob: (token, mediaId) => api.get(`/site-diary/public/${token}/media/${mediaId}/file`, { responseType: 'blob' }),
  publicPdfUrl: (token) => `${API_URL}/site-diary/public/${token}/pdf`,
  publicSign: (token, data) => api.post(`/site-diary/public/${token}/client-signature`, data),
};

// Lookup value maintenance APIs
export const lookupsAPI = {
  getTypes: () => api.get('/lookups/types'),
  getValues: (type) => api.get(`/lookups/${type}`),
  create: (type, data) => api.post(`/lookups/${type}`, data),
  update: (type, id, data) => api.patch(`/lookups/${type}/${id}`, data),
  remove: (type, id) => api.delete(`/lookups/${type}/${id}`),
  usage: (type, id) => api.get(`/lookups/${type}/${id}/usage`),
  seed: () => api.post('/lookups/seed'),
};

// System Roles (menu visibility) APIs
export const systemRolesAPI = {
  menuCatalog: () => api.get('/system-roles/menu-catalog'),
  list: () => api.get('/system-roles'),
  get: (id) => api.get(`/system-roles/${id}`),
  create: (data) => api.post('/system-roles', data),
  update: (id, data) => api.patch(`/system-roles/${id}`, data),
  remove: (id) => api.delete(`/system-roles/${id}`),
  seed: () => api.post('/system-roles/seed'),
};